import React, { useEffect, useMemo } from 'react';
import { Grid, Typography, Box, Divider, Skeleton } from '@mui/material';
import FilterBar from './filterBar';
import WebMetricsInfo from './webMetricsInfo';
import { HighchartBarChart, AreaChart } from '../../shared';
import { useDispatch, useSelector } from 'react-redux';
import {
    getWebMatrixCostDataFalse,
    getWebMatrixFalse,
    getWebMatrixGeographyDataFalse,
    getWebMatrixMultiUsageFalse,
} from '../../../redux/thunks/webMatrixThunks';
import { actions as webMatrixActions } from '../../../redux/reducers/webMatrixReducer';
import { CREATIVE_NAME_CHART_OPTIONS } from './constants';
import WebMetricsNote from './webMetricsNotes/webMetricsNote';
import CPCChart from './CPCChart';
import MapBreakDown from './mapBreakDown';
import _ from 'lodash';
const showUsersChart = [150, 61, 165];
const showAvgSessionDurationChart = [150, 61, 165];
const showPageViewChart = [150, 61, 165];
const showCtrByVisits = [150, 61, 165];
const visitsBycretiveType = [150];
const showQualityVisitsByHva = [150, 61, 165];
const showMapBreakDown = [150, 61, 165];
const showAudienceByHva = [150];
const showCostPerMin = [61, 150, 165];
const showEngagedSession = [];
const showSessionDurationByTime = [61, 165];
const showRepeatEngagementDistribution = [165];
const showMixEngagedSession = [140, 165, 61];
const hideBounceRateCompanies = [94, 103, 104, 105];
const WebSiteEngagement = () => {
    const dispatch = useDispatch();
    const { resetToInitialState } = webMatrixActions;
    const {
        isLoading,
        isGeographyLoading,
        isCostApiLoading,
        isMultiUsageLoading,
        sessionsQualityVisits,
        qvr,
        bounce_rate,
        visits_broken_down_by_channels,
        quality_visits_broken_down_by_channels,
        visits_broken_down_by_creative_type,
        cost_Per_Quality_Visit_by_Channels_StaticDataRPD,
        cost_per_quality_visit_by_channels,
        cost_per_30_seconds,
        avg_session_duration_data,
        page_views_data,
        ctr_broken_down_by_creative_type,
        qvr_by_hva_chart,
        audience_segement_by_hva,
        engagedSessionData,
        session_duration_by_time,
        repeat_engagement_distribution,
        users_series_data,
    } = useSelector((state) => state.webMatrix);
    //const { users_series_data,isLoading: dg_loading } = useSelector((state) => state.digitalEngagement);
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector(
        (state) => state.filter,
    );
    const isFinalLoading =
        isLoading ||
        isGeographyLoading ||
        isCostApiLoading ||
        isMultiUsageLoading;
    const hideQVR = [].includes(company.id);
    // const showDigitalEngagementData = [].includes(company.id);
    useEffect(() => {
        if (company.id) {
            dispatch(resetToInitialState());
            dispatch(
                getWebMatrixFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            dispatch(
                getWebMatrixCostDataFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            dispatch(
                getWebMatrixGeographyDataFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            dispatch(
                getWebMatrixMultiUsageFalse({
                    companyId: company.id,
                    startRange: startDateRange,
                    endRange: endDateRange,
                }),
            );
            // if (showDigitalEngagementData) {
            //     dispatch(getDigitalEngagement({
            //         companyId: company.id, startRange: startDateRange, endRange: endDateRange
            //     }));
            // }
        }
    }, [company, startDateRange, endDateRange]);

    const sessionsAreaChart = useMemo(() => {
        let combined = [];

        if (
            sessionsQualityVisits.chartSeries.length > 0 &&
            engagedSessionData.length > 0 &&
            showMixEngagedSession.includes(company.id)
        ) {
            combined = [
                ...sessionsQualityVisits.chartSeries,
                { name: 'Engaged Sessions', data: engagedSessionData },
            ];
        } else {
            combined = sessionsQualityVisits.chartSeries;
        }

        const filtered = combined.filter(
            (series) => Array.isArray(series.data) && series.data.length > 0,
        );

        return _.cloneDeep(filtered);
    }, [sessionsQualityVisits, engagedSessionData, company.id]);

    const handleChartOptions = (
        xAxisTitle = '',
        yAxisTitle = '',
        xAxisCategories = [],
        yAxisMax = null,
        colors = [],
        isPercentage = false,
        isCurrency = false,
    ) => {
        const options = {
            title: { text: null },
            subtitle: { text: null },
            chart: {
                backgroundColor: 'transparent',
                type: 'column',
            },
            colors: colors,
            mapNavigation: {
                enabled: false,
                enableButtons: false,
            },
            navigation: { buttonOptions: { enabled: false } },
            credits: { enabled: false },
            tooltip: {
                enabled: true,
                outside: true,
                followPointer: true,
                followTouchMove: true,
                formatter: function () {
                    const [fullMonth, year] = this.key.split(' ');
                    const shortMonth = new Date(
                        `${fullMonth} 1, 2000`,
                    ).toLocaleString('default', { month: 'short' });
                    const pointValue = isPercentage
                        ? `${this.y}%`
                        : isCurrency
                          ? `$${this.y}`
                          : this.y;
                    return `<span style="font-size: 10px">${shortMonth} ${year}</span><br/>
                            <span style="font-size: 12px">${this.series.name}: <b>${pointValue}</b><br/></span>`;
                },
                style: { zIndex: 9999 },
            },
            legend: { enabled: false },
            dataLabels: { enabled: false },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderRadius: 3,
                    maxPointWidth: 50,
                },
            },

            xAxis: {
                categories: xAxisCategories,
                title: { text: xAxisTitle },
                lineColor: 'transparent',
            },
            yAxis: {
                min: 0,
                title: { text: yAxisTitle },
                stackLabels: { enabled: false },
                lineColor: 'transparent',
                gridLineWidth: 0,
            },
        };

        if (yAxisMax) {
            return { ...options, yAxis: { ...options.yAxis, max: yAxisMax } };
        }

        if (isCurrency) {
            return {
                ...options,
                yAxis: {
                    ...options.yAxis,
                    labels: {
                        formatter: function () {
                            return '$' + this.value;
                        },
                    },
                },
            };
        }

        return options;
    };

    const handleStaticData = () => {
        let staticData = {};
        const showStatic = [41, 42, 3, 16, 37, 51, 45];
        if (company.id === 150) {
            staticData = {
                data: cost_Per_Quality_Visit_by_Channels_StaticDataRPD.dataSets,
                xAxisCategories:
                    cost_Per_Quality_Visit_by_Channels_StaticDataRPD.xAxisCategories,
                color: cost_Per_Quality_Visit_by_Channels_StaticDataRPD.colors,
            };
        }
        if (company.id === 61) {
            staticData = {
                data: cost_Per_Quality_Visit_by_Channels_StaticDataRPD.dataSets,
                xAxisCategories:
                    cost_Per_Quality_Visit_by_Channels_StaticDataRPD.xAxisCategories,
                color: cost_Per_Quality_Visit_by_Channels_StaticDataRPD.colors,
            };
        }
        return { staticData, showStaticData: showStatic.includes(company.id) };
    };

    const handleHideCostChart = () => {
        const companies = [61, 150, 165, 91]; //[37, 3, 16,40, 45, 42,61];
        return companies.includes(company.id);
    };
    const handleVisitChannelChart = () => {
        const companies = [40];
        return !companies.includes(company.id);
    };
    const handleHideBrokenChannelChart = () => {
        const companies = [40];
        return !companies.includes(company.id);
    };

    const areaChartYaxisLables = {
        session_quality_visits: {
            61: 'No. of Sessions, Quality Visits',
            165: 'No. of Sessions, Quality Visits and Engaged Sessions',
            default: 'No. of Sessions and Quality Visits',
        },
        avg_session_duration: {
            61: 'Sessions Duration',
            default: 'Sessions Duration',
        },
        page_view: {
            61: 'Page views',
            default: 'Page views',
        },
        newUser_vs_retuningUser: {
            61: 'New User Vs Returning User',
            default: 'New User Vs Returning User',
        },
        engaged_session: {
            61: 'Engaged Sessions',
            default: 'Engaged Sessions',
        },
    };

    const handleAreaChartOptions = (companyId, chartType) => {
        const options = _.cloneDeep(CREATIVE_NAME_CHART_OPTIONS);

        options.yaxis.title.text =
            areaChartYaxisLables[chartType]?.[companyId] ||
            areaChartYaxisLables[chartType]?.default;

        return options;
    };

    const chartHeaderItems = {
        61: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visit Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per First Quality Visit by Channels',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
        165: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visit Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per First Quality Visit by Channels',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
        150: {
            SessionAndQVR: 'Sessions and Quality Visits',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visit Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per First Quality Visit by Channels',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
        default: {
            SessionAndQVR: 'Sessions And Quality Visits',
            qvr: 'Quality Visits Rate (%)',
            bounce_rate: 'Bounce Rate (%)',
            qvrBrokenByChannel: 'Quality Visits Broken Down by Channels (%)',
            avg_session_duration: 'Average Session Duration (sec.)',
            newUser_vs_retuningUser: 'New User Vs Returning User',
            page_view: 'Page Views',
            cqvr: 'Cost Per Quality Visit by Channels',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
    };

    const yAxisLabelsItems = {
        61: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate',
            bounce_rate: 'Bounce Rate',
            qvrBrokenByChannel: 'Quality Visits by Channels',
            avg_session_duration: 'Average Session Duration',
            newUser_vs_retuningUser: 'Total Users',
            page_view: 'Page Views',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
        165: {
            SessionAndQVR: 'Sessions, Quality Visits and Engaged Sessions',
            qvr: 'Quality Visits Rate',
            bounce_rate: 'Bounce Rate',
            qvrBrokenByChannel: 'Quality Visits by Channels',
            avg_session_duration: 'Average Session Duration',
            newUser_vs_retuningUser: 'Total Users',
            page_view: 'Page Views',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
        150: {
            SessionAndQVR: 'Sessions And Quality Visits',
            qvr: 'Quality Visits Rate',
            bounce_rate: 'Bounce Rate',
            qvrBrokenByChannel: 'Quality Visits by Channels',
            avg_session_duration: 'Average Session Duration',
            newUser_vs_retuningUser: 'Total Users',
            page_view: 'Page Views',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
        default: {
            SessionAndQVR: 'Sessions And Quality Visits',
            qvr: 'Quality Visits Rate',
            bounce_rate: 'Bounce Rate',
            qvrBrokenByChannel: 'Quality Channel Visits',
            avg_session_duration: 'Average Session Duration',
            newUser_vs_retuningUser: 'Total Users',
            page_view: 'Page Views',
            SessionAndQVREngaged:
                'Sessions, Quality Visits and Engaged Sessions',
        },
    };

    const isPercentNeedItems = {
        61: {
            qvr: true,
            bounce_rate: true,
            qvrBrokenByChannel: true,
            avg_session_duration: false,
            newUser_vs_retuningUser: false,
            page_view: false,
        },
        150: {
            qvr: false,
            bounce_rate: false,
            qvrBrokenByChannel: true,
            avg_session_duration: false,
            newUser_vs_retuningUser: false,
            page_view: false,
        },
        default: {
            qvr: true,
            bounce_rate: true,
            qvrBrokenByChannel: true,
            avg_session_duration: false,
            newUser_vs_retuningUser: false,
            page_view: false,
        },
    };
    const isPercentNeeded =
        isPercentNeedItems[company.id] || isPercentNeedItems.default;
    const chartTitles =
        chartHeaderItems[company.id] || chartHeaderItems.default;
    const yAxisLabels =
        yAxisLabelsItems[company.id] || yAxisLabelsItems.default;
    const renderGraph = (
        metricsType,
        title,
        graph,
        isPercentage = false,
        isCurrency = false,
    ) => (
        <Grid item xs={6}>
            <Box className="web-metrics-graph-container">
                <Box className="padding-tb-24-rl-20">
                    <Typography className="text-md-semi-bold gray-900">
                        {isPercentage
                            ? title + ' (%)'
                            : isCurrency
                              ? title + ' ($)'
                              : title}
                    </Typography>
                </Box>
                <Divider className="border-gray-200" />
                <Box className="padding-24px min-height-402px">
                    {isFinalLoading ? (
                        <Skeleton height={'400px'} variant="rectangular" />
                    ) : (
                        graph
                    )}
                </Box>
            </Box>
        </Grid>
    );

    return (
        <>
            <FilterBar />
            <Grid container spacing={2}>
                {/* Webmetrics */}
                <Grid item xs={12}>
                    <WebMetricsInfo />
                </Grid>
                <Grid item xs={12}>
                    <WebMetricsNote />
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={'24px'}>
                        {/* Sessions & Quality Visits */}
                        <Grid item xs={6}>
                            <Box className="web-metrics-graph-container h-100">
                                <Box className="padding-tb-24-rl-20">
                                    <Typography className="text-md-semi-bold gray-900">
                                        {chartTitles.SessionAndQVR}
                                    </Typography>
                                </Box>
                                <Divider className="border-gray-200" />
                                <Box className="padding-24px min-height-402px mt-40px">
                                    {isFinalLoading ? (
                                        <Skeleton
                                            height={'350px'}
                                            variant="rectangular"
                                        />
                                    ) : (
                                        <AreaChart
                                            chartOptions={handleAreaChartOptions(
                                                company.id,
                                                'session_quality_visits',
                                            )}
                                            chartSeries={sessionsAreaChart}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        {/* new users vs returning users */}
                        {showUsersChart.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            {
                                                chartTitles.newUser_vs_retuningUser
                                            }
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'350px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'newUser_vs_retuningUser',
                                                )}
                                                chartSeries={_.cloneDeep(
                                                    users_series_data,
                                                )}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                        {!hideBounceRateCompanies.includes(company.id) ? (
                            <Grid item xs={6}>
                                {/* Bounce Rate */}
                                <Box className="web-metrics-graph-container">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            {chartTitles.bounce_rate}
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <HighchartBarChart
                                                chartOptions={handleChartOptions(
                                                    'Months',
                                                    yAxisLabels.bounce_rate,
                                                    bounce_rate.xAxisCategories,
                                                    null,
                                                    bounce_rate.colors,
                                                    isPercentNeeded.bounce_rate,
                                                    false,
                                                )}
                                                key={`chart-${bounce_rate.dataSets}-${company.id}`}
                                                seriesData={
                                                    bounce_rate.dataSets
                                                }
                                                seriesName={
                                                    yAxisLabels.bounce_rate
                                                }
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        ) : null}
                        {/* Visits Broken Down by Channels */}
                        {handleVisitChannelChart() &&
                            renderGraph(
                                'visit_broke_down_by_channels',
                                'Visits Broken Down by Channels',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Channel Visits',
                                        visits_broken_down_by_channels.xAxisCategories,
                                        100,
                                        visits_broken_down_by_channels.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        visits_broken_down_by_channels.dataSets
                                    }
                                />,
                                true,
                            )}
                        {/* Avg session duration */}
                        {showAvgSessionDurationChart.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            {chartTitles.avg_session_duration}
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'avg_session_duration',
                                                )}
                                                chartSeries={[
                                                    {
                                                        name: 'Avg. session duration',
                                                        data: avg_session_duration_data,
                                                    },
                                                ]}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                        {/* Session Duration by Time */}
                        {showSessionDurationByTime.includes(company.id) &&
                            renderGraph(
                                'session_duration by time',
                                'Sessions Broken Down by Duration',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Session Duration',
                                        session_duration_by_time.xAxisCategories,
                                        null,
                                        session_duration_by_time.colors,
                                        false,
                                        false,
                                    )}
                                    seriesData={
                                        session_duration_by_time.dataSets
                                    }
                                />,
                                false,
                            )}
                        {/* Cost Per 30 Second of Session Visit */}
                        {showCostPerMin.includes(company.id) && (
                            <Grid item xs={6}>
                                <CPCChart
                                    chart_data={cost_per_30_seconds}
                                    isLoading={isFinalLoading}
                                />
                            </Grid>
                        )}
                        {/* Page View */}
                        {showPageViewChart.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            {chartTitles.page_view}
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'page_view',
                                                )}
                                                chartSeries={[
                                                    {
                                                        name: 'Page View',
                                                        data: page_views_data,
                                                    },
                                                ]}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}

                        {/* QVR */}
                        {hideQVR
                            ? null
                            : renderGraph(
                                  'qvr',
                                  chartTitles.qvr,
                                  <HighchartBarChart
                                      chartOptions={handleChartOptions(
                                          'Months',
                                          yAxisLabels.qvr,
                                          qvr.xAxisCategories,
                                          null,
                                          qvr.colors,
                                          isPercentNeeded.qvr,
                                          false,
                                      )}
                                      seriesData={qvr.dataSets}
                                      seriesName={yAxisLabels.qvr}
                                  />,
                              )}

                        {/* Quality Visits Broken Down by Actions  */}
                        {showQualityVisitsByHva.includes(company.id) &&
                            renderGraph(
                                'ctr_broken_down_by_creative_type',
                                'Quality Visits Broken Down by Actions',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Quality Visits',
                                        qvr_by_hva_chart.xAxisCategories,
                                        null,
                                        qvr_by_hva_chart.colors,
                                        false,
                                        false,
                                    )}
                                    seriesData={qvr_by_hva_chart.dataSets}
                                />,
                                false,
                            )}
                        {/* Quality Visits Broken Down by Audience Segments */}
                        {showAudienceByHva.includes(company.id) &&
                            renderGraph(
                                'audience_segement_by_hva',
                                'Quality Visits Broken Down by Audience Segments',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Audience',
                                        audience_segement_by_hva.xAxisCategories,
                                        100,
                                        audience_segement_by_hva.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        audience_segement_by_hva.dataSets
                                    }
                                />,
                                true,
                            )}

                        {/* Quality Visits Broken Down by Channels */}
                        {handleHideBrokenChannelChart() &&
                            renderGraph(
                                'quality_visit_broke_down_by_channels',
                                chartTitles.qvrBrokenByChannel,
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        yAxisLabels.qvrBrokenByChannel,
                                        quality_visits_broken_down_by_channels.xAxisCategories,
                                        100,
                                        quality_visits_broken_down_by_channels.colors,
                                        isPercentNeeded.qvrBrokenByChannel,
                                        false,
                                    )}
                                    seriesData={
                                        quality_visits_broken_down_by_channels.dataSets
                                    }
                                />,
                            )}
                        {/* Cost Per Quality Visit by Channels */}
                        {
                            // need to delete thus condition based rendering and remove above one
                            handleHideCostChart() &&
                                (handleStaticData().showStaticData
                                    ? renderGraph(
                                          'cost_per_quality_visit_by_channel',
                                          chartTitles.cqvr,
                                          <HighchartBarChart
                                              chartOptions={handleChartOptions(
                                                  'Months',
                                                  'Quality Cost Visits',
                                                  handleStaticData().staticData
                                                      .xAxisCategories,
                                                  null,
                                                  handleStaticData().staticData
                                                      .color,
                                                  false,
                                                  true,
                                              )}
                                              seriesData={
                                                  handleStaticData().staticData
                                                      .data
                                              }
                                          />,
                                          false,
                                          true,
                                      )
                                    : renderGraph(
                                          'cost_per_quality_visit_by_channel',
                                          'Cost Per Quality Visit by Channels',
                                          <HighchartBarChart
                                              chartOptions={handleChartOptions(
                                                  'Months',
                                                  'Quality Cost Visits',
                                                  cost_per_quality_visit_by_channels.xAxisCategories,
                                                  null,
                                                  cost_per_quality_visit_by_channels.colors,
                                                  false,
                                                  true,
                                              )}
                                              seriesData={
                                                  cost_per_quality_visit_by_channels.dataSets
                                              }
                                          />,
                                          false,
                                          true,
                                      ))
                        }
                        {/* CTR broken down by Creative Types */}
                        {showCtrByVisits.includes(company.id) &&
                            renderGraph(
                                'ctr_broken_down_by_creative_type',
                                'CTR Broken Down by Creative Types',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'CTR',
                                        ctr_broken_down_by_creative_type.xAxisCategories,
                                        100,
                                        quality_visits_broken_down_by_channels.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        ctr_broken_down_by_creative_type.dataSets
                                    }
                                />,
                                true,
                            )}

                        {/* Visits Broken Down by Creative Type */}
                        {visitsBycretiveType.includes(company.id) &&
                            renderGraph(
                                'visit_broke_down_by_creative_type',
                                'Visits Broken Down by Creative Type',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Creative Type',
                                        visits_broken_down_by_creative_type.xAxisCategories,
                                        100,
                                        visits_broken_down_by_creative_type?.colors,
                                        true,
                                        false,
                                    )}
                                    seriesData={
                                        visits_broken_down_by_creative_type.dataSets
                                    }
                                />,
                                true,
                            )}

                        {/* Engaged Session  */}
                        {showEngagedSession.includes(company.id) && (
                            <Grid item xs={6}>
                                <Box className="web-metrics-graph-container h-100">
                                    <Box className="padding-tb-24-rl-20">
                                        <Typography className="text-md-semi-bold gray-900">
                                            Engaged Sessions
                                        </Typography>
                                    </Box>
                                    <Divider className="border-gray-200" />
                                    <Box className="padding-24px min-height-402px mt-40px">
                                        {isFinalLoading ? (
                                            <Skeleton
                                                height={'400px'}
                                                variant="rectangular"
                                            />
                                        ) : (
                                            <AreaChart
                                                chartOptions={handleAreaChartOptions(
                                                    company.id,
                                                    'engaged_session',
                                                )}
                                                chartSeries={[
                                                    {
                                                        name: 'Engaged Sessions',
                                                        data: engagedSessionData,
                                                    },
                                                ]}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        )}

                        {/* Repeat Engagement Distribution */}
                        {showRepeatEngagementDistribution.includes(
                            company.id,
                        ) &&
                            renderGraph(
                                'repeat_engagement_distribution',
                                'Repeat Engagement Distribution',
                                <HighchartBarChart
                                    chartOptions={handleChartOptions(
                                        'Months',
                                        'Repeat Engagement Distribution',
                                        repeat_engagement_distribution.xAxisCategories,
                                        null,
                                        repeat_engagement_distribution.colors,
                                        false,
                                        false,
                                    )}
                                    seriesData={
                                        repeat_engagement_distribution.dataSets
                                    }
                                />,
                                false,
                            )}
                        {showMapBreakDown.includes(company.id) && (
                            <Grid item xs={12}>
                                <MapBreakDown />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default WebSiteEngagement;
